// When does the camera go green, and when may it take the photo by itself?
// A pure reducer stepped once per frame sample. Unit-tested.
//
//   searching → framing → verifying → locked → snapped
//
//   - The real car has to fit the target (framing.ts judgeFit) for 2 samples
//     in a row; 2 misses unlock (no flicker on a single noisy detection).
//   - Then the car-view brain checks it's the corner asked for
//     ("verifying"). Right, or not sure, locks; wrong corner or standing
//     square-on doesn't. No reading within VIEW_WAIT_MS counts as not sure:
//     the brain never keeps anyone waiting.
//   - Auto-snap after LOCK_HOLD_MS of continuous lock while still, sharp,
//     lit and free of glare (the countdown restarts whenever any of those
//     breaks), never sooner than MIN_OPEN_MS after the shot opened. A
//     reflection never gets an auto-photo (a tap still works); after
//     GLARE_HINT_MS the coach says so.
//   - The brain refusing a car that fits for VIEW_GIVE_UP_MS (it can be
//     wrong about an unusual car) hands the shutter to the person.
//   - Never re-shoots the previous spot: except for the first shot of the
//     walk, the person must have moved since the shot opened (the framing
//     broke, or the view changed enough) before an auto-snap is allowed.
//   - After MANUAL_HINT_MS without a lock, the coach asks for a tap.

export const LOCK = {
  PASS_TO_LOCK: 2,
  FAIL_TO_UNLOCK: 2,
  LOCK_HOLD_MS: 700,
  MIN_OPEN_MS: 1500,
  MANUAL_HINT_MS: 8000,
  VIEW_WAIT_MS: 1200,
  VIEW_GIVE_UP_MS: 8000,
  GLARE_HINT_MS: 2500,
  // Accumulated mean-abs thumbnail difference that means "walked somewhere".
  MOVE_THRESHOLD: 120,
} as const

export type LockPhase = "searching" | "framing" | "verifying" | "locked" | "snapped"

// The car-view brain's live answer: pending = no reading yet.
export type LockView = "right" | "unsure" | "wrong" | "angle" | "pending"

export interface LockState {
  phase: LockPhase
  pass: number
  fail: number
  lockedAt: number | null
  // When the fit first held and the brain was asked.
  verifyingSince: number | null
  // Countdown start: reset whenever the phone moves while locked.
  steadySince: number | null
  openedAt: number
  moved: boolean
  motionSum: number
  // Since when a reflection has been in the way (null: none).
  glareSince: number | null
}

export interface LockInput {
  t: number
  hasCar: boolean
  // The real car fits the target (judgeFit).
  fit: boolean
  view: LockView
  still: boolean
  sharp: boolean
  lit: boolean
  // A reflection blows out part of the car.
  glare: boolean
  // Thumbnail motion since the previous sample (0 when unknown).
  motion: number
}

export interface LockOptions {
  autoSnap: boolean
  // First shot of the walk: nothing to move away from yet.
  firstShot: boolean
}

export function initialLock(t: number, firstShot: boolean): LockState {
  return {
    phase: "searching",
    pass: 0,
    fail: 0,
    lockedAt: null,
    verifyingSince: null,
    steadySince: null,
    openedAt: t,
    moved: firstShot,
    motionSum: 0,
    glareSince: null,
  }
}

// The brain's answer lets it lock (right, not sure, or no answer in time).
function viewAllows(view: LockView, verifyingSince: number | null, t: number): boolean {
  if (view === "right" || view === "unsure") return true
  if (view === "pending") return verifyingSince !== null && t - verifyingSince >= LOCK.VIEW_WAIT_MS
  return false
}

export function stepLock(
  s: LockState,
  input: LockInput,
  opts: LockOptions,
): { state: LockState; snap: boolean } {
  if (s.phase === "snapped") return { state: s, snap: false }
  const motionSum = s.motionSum + (Number.isFinite(input.motion) ? input.motion : 0)
  const ok = input.hasCar && input.fit
  const pass = ok ? s.pass + 1 : 0
  const fail = ok ? 0 : s.fail + 1
  // "Moved" = the view changed since the shot opened: the framing broke for
  // a moment, or enough motion piled up (walking to the next corner).
  const moved = s.moved || motionSum >= LOCK.MOVE_THRESHOLD || fail >= LOCK.FAIL_TO_UNLOCK
  let phase: LockPhase = s.phase
  let lockedAt = s.lockedAt
  let verifyingSince = s.verifyingSince
  let steadySince = s.steadySince
  const lost = fail >= LOCK.FAIL_TO_UNLOCK

  if (phase === "locked" || phase === "verifying") {
    if (lost) {
      phase = input.hasCar ? "framing" : "searching"
      lockedAt = null
      verifyingSince = null
      steadySince = null
    }
  } else if (pass >= LOCK.PASS_TO_LOCK) {
    phase = "verifying"
    verifyingSince = input.t
  } else {
    phase = input.hasCar ? "framing" : "searching"
  }

  if (phase === "verifying" && viewAllows(input.view, verifyingSince, input.t)) {
    phase = "locked"
    lockedAt = input.t
    steadySince = input.still ? input.t : null
  } else if (phase === "locked" && (input.view === "wrong" || input.view === "angle")) {
    // The brain changed its mind: not the corner asked for after all.
    phase = "verifying"
    lockedAt = null
    steadySince = null
  }

  // The countdown runs only while a good photo is possible right now.
  const good = input.still && input.sharp && input.lit && !input.glare
  if (phase === "locked") {
    if (!good) steadySince = null
    else if (steadySince === null) steadySince = input.t
  }

  const glareSince = input.glare ? (s.glareSince ?? input.t) : null
  const ready =
    phase === "locked" &&
    opts.autoSnap &&
    moved &&
    good &&
    steadySince !== null &&
    input.t - steadySince >= LOCK.LOCK_HOLD_MS &&
    input.t - s.openedAt >= LOCK.MIN_OPEN_MS

  const state: LockState = {
    phase: ready ? "snapped" : phase,
    pass: Math.min(pass, 99),
    fail: Math.min(fail, 99),
    lockedAt,
    verifyingSince,
    steadySince,
    openedAt: s.openedAt,
    moved,
    motionSum,
    glareSince,
  }
  return { state, snap: ready }
}

// 0–1 fill of the countdown ring around the shutter (empty with a reflection:
// no photo is coming by itself).
export function lockProgress(s: LockState, t: number): number {
  if (s.phase === "snapped") return 1
  if (s.phase !== "locked" || s.steadySince === null) return 0
  return Math.min(1, Math.max(0, (t - s.steadySince) / LOCK.LOCK_HOLD_MS))
}

export function wantsManualHint(s: LockState, t: number): boolean {
  return s.phase !== "locked" && s.phase !== "snapped" && t - s.openedAt >= LOCK.MANUAL_HINT_MS
}

// The brain has refused a car that fits for a while: time to say "tap".
export function viewStuck(s: LockState, t: number): boolean {
  return s.phase === "verifying" && s.verifyingSince !== null && t - s.verifyingSince >= LOCK.VIEW_GIVE_UP_MS
}

// A reflection that won't go: time to say "tap the button".
export function glareStuck(s: LockState, t: number): boolean {
  return s.glareSince !== null && t - s.glareSince >= LOCK.GLARE_HINT_MS
}
